import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { useClients } from '../../hooks/useClients';
import SubscriptionsTable from './SubscriptionsTable';
import ManualSubscriptionForm from './ManualSubscriptionForm';
import DateRangePicker, { useDateRangePresets } from '../common/DateRangePicker';
import HelpTooltip from '../common/HelpTooltip';
import { Search, Plus, Filter } from 'lucide-react';
import { SubscriptionStatus } from '../../types';

export default function SubscriptionsPage() {
  const [searchParams] = useSearchParams();
  const initialClientId = searchParams.get('clientId') || undefined;

  const [showFilters, setShowFilters] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(initialClientId);

  const presets = useDateRangePresets();

  // Fetch all clients for the filter dropdown (no pagination limit)
  const { clients, loading: clientsLoading } = useClients({ pageSize: 1000 });

  const {
    subscriptions,
    total,
    page,
    totalPages,
    loading,
    error,
    filters,
    setFilters,
    refetch,
  } = useSubscriptions({ clientId: selectedClientId });

  // Sync selectedClientId with filters when changed externally (e.g., URL param)
  useEffect(() => {
    if (initialClientId && initialClientId !== selectedClientId) {
      setSelectedClientId(initialClientId);
    }
  }, [initialClientId]);

  const handleClientFilter = (clientId: string) => {
    const newClientId = clientId || undefined;
    setSelectedClientId(newClientId);
    setFilters({ ...filters, clientId: newClientId, page: 1 });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchTerm, page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleStatusFilter = (status: SubscriptionStatus | '') => {
    setFilters({
      ...filters,
      status: status || undefined,
      page: 1,
    });
  };

  const handleDateRangeApply = () => {
    setFilters({
      ...filters,
      renewalDateFrom: fromDate || undefined,
      renewalDateTo: toDate || undefined,
      page: 1,
    });
  };

  const applyPreset = (preset: { fromDate: string; toDate: string }) => {
    setFromDate(preset.fromDate);
    setToDate(preset.toDate);
    setFilters({
      ...filters,
      renewalDateFrom: preset.fromDate,
      renewalDateTo: preset.toDate,
      page: 1,
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFromDate('');
    setToDate('');
    setSelectedClientId(undefined);
    setFilters({});
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              Subscriptions
            </h1>
            <HelpTooltip
              text="View all license subscriptions across your clients. Subscriptions come from two sources: Pax8 (your reseller purchases) and MS Direct (licenses bought directly from Microsoft). Click any subscription to see details and manage settings."
              
            />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage license subscriptions and track renewals
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-1">
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Manual Subscription
          </button>
          <HelpTooltip
            text="Manually add a subscription that isn't synced from Pax8. Useful for tracking non-Pax8 licenses or subscriptions from other vendors."
            size="sm"
          />
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by product name or SKU..."
                className="input pl-10"
              />
            </div>
          </form>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary ${showFilters ? 'bg-muted' : ''}`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="label mb-0">Customer</label>
                  <HelpTooltip text="Filter subscriptions by a specific client/customer." size="sm" />
                </div>
                <select
                  value={selectedClientId || ''}
                  onChange={(e) => handleClientFilter(e.target.value)}
                  className="input"
                  disabled={clientsLoading}
                >
                  <option value="">All Customers</option>
                  {clients
                    .sort((a, b) => a.companyName.localeCompare(b.companyName))
                    .map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.companyName}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="label mb-0">Status</label>
                  <HelpTooltip text="Active: Currently active. Suspended: Temporarily disabled. Cancelled: Terminated. Pending Cancellation: Cancellation requested but not yet processed." size="sm" />
                </div>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleStatusFilter(e.target.value as SubscriptionStatus | '')}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="pending-cancellation">Pending Cancellation</option>
                </select>
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="label mb-0">Auto-Renew</label>
                  <HelpTooltip text="ON: Subscription will automatically renew. OFF: Subscription will expire at end of term. Check this in Microsoft Admin Center for each customer." size="sm" />
                </div>
                <select
                  value={filters.autoRenew === undefined ? '' : filters.autoRenew ? 'true' : 'false'}
                  onChange={(e) => setFilters({
                    ...filters,
                    autoRenew: e.target.value === '' ? undefined : e.target.value === 'true',
                    page: 1
                  })}
                  className="input"
                >
                  <option value="">All</option>
                  <option value="true">Auto-Renew ON</option>
                  <option value="false">Auto-Renew OFF</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <DateRangePicker
                  fromDate={fromDate}
                  toDate={toDate}
                  onFromDateChange={setFromDate}
                  onToDateChange={setToDate}
                  label="Renewal Date Range"
                />
              </div>
              <div className="flex items-end space-x-2">
                <button onClick={handleDateRangeApply} className="btn-primary flex-1">
                  Apply
                </button>
                <button onClick={clearFilters} className="btn-secondary flex-1">
                  Clear
                </button>
              </div>
            </div>

            {/* Date Presets */}
            <div>
              <label className="label">Quick Presets</label>
              <div className="flex flex-wrap gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => applyPreset(preset.getRange())}
                    className="px-3 py-1 text-xs rounded-full bg-muted text-foreground hover:bg-accent"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Table */}
      <SubscriptionsTable
        subscriptions={subscriptions}
        loading={loading}
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={50}
        onPageChange={handlePageChange}
      />

      {/* Create Subscription Modal */}
      {showCreateForm && (
        <ManualSubscriptionForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
